import { useState, type FormEvent } from 'react';
import { api, userAvatarUrl } from '../lib/api';
import { useSync } from '../lib/sync';
import { avatarGradient, initials } from '../lib/format';
import { Dialog, ImagePicker, useToast } from './ui';

/** Eigenes Profil: Name und Profilbild (gilt in allen Teams). */
export function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSync();
  const toast = useToast();
  const [name, setName] = useState(me.user.name);
  const [avatarAt, setAvatarAt] = useState(me.user.avatarAt);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  const reload = () => window.dispatchEvent(new Event('machs:reload-me'));

  async function upload(file: File) {
    setBusy(true);
    try {
      const form = new FormData();
      form.set('file', file, file.name);
      const r = await api<{ avatarAt: string }>('/profile/avatar', { method: 'PUT', body: form });
      setAvatarAt(r.avatarAt);
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Hochladen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    await api('/profile/avatar', { method: 'DELETE' });
    setAvatarAt(null);
    reload();
  }

  async function deleteAccount() {
    try {
      await api('/profile', { method: 'DELETE', json: { confirmEmail: confirmEmail.trim() } });
      window.dispatchEvent(new Event('machs:logout'));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && name.trim() !== me.user.name) {
      await api('/profile', { method: 'PUT', json: { name: name.trim() } });
      reload();
    }
    onClose();
  }

  return (
    <Dialog title="Dein Profil" onClose={onClose}>
      <form className="flex flex-col gap-5" onSubmit={submit}>
        <ImagePicker
          label={busy ? 'Wird hochgeladen …' : 'Profilbild'}
          preview={userAvatarUrl(me.user.id, avatarAt)}
          fallback={{ name: initials(name || me.user.name), gradient: avatarGradient(me.user.id) }}
          onPick={(f) => void upload(f)}
          onRemove={() => void remove()}
        />
        <label className="field">Name<input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required /></label>
        <p className="m-0 text-[13px] text-[var(--muted)]">Name und Bild sehen alle, mit denen du in Aufgaben zusammenarbeitest.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary">Speichern</button>
        </div>
      </form>

      <div className="mt-6 border-t border-[var(--line)] pt-4">
        {!deleting ? (
          <button className="btn btn-ghost text-[var(--problem)]" onClick={() => setDeleting(true)}>Konto löschen</button>
        ) : (
          <div className="flex flex-col gap-3">
            <b className="text-[15px] text-[var(--problem)]">Konto endgültig löschen</b>
            <p className="m-0 text-[13.5px] text-[var(--muted)]">
              Dein Konto und dein Profilbild werden gelöscht. Teams, in denen du allein bist, werden mit allen Aufgaben
              und Dateien gelöscht. In Teams mit anderen Mitgliedern bleiben deine Beiträge mit deinem Namen erhalten.
              Das lässt sich nicht rückgängig machen.
            </p>
            <label className="field">
              Zur Bestätigung deine E-Mail-Adresse
              <input className="input" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={me.user.email} autoComplete="off" />
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => (setDeleting(false), setConfirmEmail(''))}>Abbrechen</button>
              <button className="btn btn-danger" disabled={confirmEmail.trim().toLowerCase() !== me.user.email.toLowerCase()} onClick={() => void deleteAccount()}>
                Endgültig löschen
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
